/**
 * Shared types for LeadHunter frontend
 */

// User types
export interface User {
  id: number
  email: string
  email_verified: boolean
  is_verified: boolean
  role: string
  created_at: string
}

// API Error types
export interface ApiErrorResponse {
  detail?: string | { msg: string }[]
  message?: string
}

export interface ApiError {
  response?: {
    data?: ApiErrorResponse
    status?: number
  }
  message?: string
}

// Utility function to extract error message
export function getErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error'

  const apiError = error as ApiError

  // Check for API response error
  if (apiError.response?.data?.detail) {
    const detail = apiError.response.data.detail
    if (typeof detail === 'string') {
      return detail
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'Validation error'
    }
  }

  // Check for response message
  if (apiError.response?.data?.message) {
    return apiError.response.data.message
  }

  // Check for error message
  if (apiError.message) {
    return apiError.message
  }

  return 'An unexpected error occurred'
}

// Lead types
export interface Lead {
  id: number
  user_id: number
  channel_id: number
  telegram_user_id: number
  username?: string
  first_name?: string
  last_name?: string
  message_text: string
  detected_need?: string
  relevance_score: number
  status: LeadStatus
  created_at: string
  updated_at: string
}

export type LeadStatus = 'new' | 'contacted' | 'responded' | 'converted' | 'rejected'

// Channel types
export interface Channel {
  id: number
  user_id: number
  telegram_id: number
  username?: string
  title: string
  channel_type?: string
  members_count?: number
  description?: string
  relevance_score?: number
  status: ChannelStatus
  added_at: string
  last_parsed_at?: string
}

export type ChannelStatus = 'discovered' | 'monitored' | 'paused' | 'ignored'

// Message types
export interface GeneratedMessage {
  id: number
  lead_id: number
  content: string
  tone: string
  status: MessageStatus
  created_at: string
  sent_at?: string
}

export type MessageStatus = 'draft' | 'approved' | 'sent' | 'delivered' | 'read' | 'failed'

// Stats types
export interface DashboardStats {
  total_leads: number
  new_leads: number
  contacted_leads: number
  converted_leads: number
  total_channels: number
  active_channels: number
  messages_sent: number
  response_rate: number
}

// Pagination
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
  pages: number
}
